import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useState } from "react";
import {
  pipelineService,
  type FranceToParisMappedFilters,
  type FranceToParisMappedStats,
} from "../../../../api/pipeline.service.js";
import { useFranceToParisMappedCopy } from "./useFranceToParisMappedCopy.js";
import styles from "../PipelineBrowser.module.css";

const DEFAULT_BATCH_SIZE = 1000;
const MIN_BATCH_SIZE = 1;
const MAX_BATCH_SIZE = 5000;

function clampBatchSize(value: number): number {
  if (!Number.isFinite(value)) return MIN_BATCH_SIZE;
  return Math.min(Math.max(Math.trunc(value), MIN_BATCH_SIZE), MAX_BATCH_SIZE);
}

type RunPanelProps = {
  filters: FranceToParisMappedFilters;
};

// Loads stats for the current filters + mappings, and drives the batch loop.
export function RunPanel({ filters }: RunPanelProps) {
  const [batchSize, setBatchSize] = useState(String(DEFAULT_BATCH_SIZE));
  const [stats, setStats] = useState<FranceToParisMappedStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      setStats(await pipelineService.getFranceToParisMappedStats(filters));
    } catch (err) {
      setStatsError(err instanceof Error ? err.message : "Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  }, [filters]);

  // Refresh stats whenever the effective filters/mappings change.
  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const {
    running,
    progress,
    error: loopError,
    start,
    stop,
  } = useFranceToParisMappedCopy(loadStats);

  const handleStart = useCallback(() => {
    start(filters, clampBatchSize(Number(batchSize)));
  }, [start, filters, batchSize]);

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Copy France → Paris (mapped)</div>

      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>Batch size:</span>
        <input
          className={styles.filterInput}
          type="number"
          min={MIN_BATCH_SIZE}
          max={MAX_BATCH_SIZE}
          value={batchSize}
          disabled={running}
          onChange={e => setBatchSize(e.target.value)}
          placeholder="Batch size"
          style={{ width: 100 }}
        />
      </div>

      <div className={styles.syncStateRow}>
        {statsLoading ? (
          <span>Loading stats…</span>
        ) : statsError ? (
          <span>Error: {statsError}</span>
        ) : (
          <span>
            Total: <strong>{stats?.total ?? "-"}</strong> · Copied:{" "}
            <strong>{stats?.copied ?? "-"}</strong> · Remaining:{" "}
            <strong>{stats?.remaining ?? "-"}</strong>
          </span>
        )}
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={running || statsLoading}
          onClick={() => void loadStats()}
        >
          Refresh
        </Button>
      </div>

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={running}
          onClick={handleStart}
        >
          {running ? `Working… (batch ${progress.batch})` : "Start"}
        </Button>
        <Button variant="outlined" color="error" size="small" disabled={!running} onClick={stop}>
          Stop
        </Button>
      </div>

      {(running || progress.batch > 0) && (
        <div className={styles.syncStateMessage}>
          Copied {progress.copied} so far, remaining {progress.remaining ?? "-"}
        </div>
      )}

      {loopError && <div className={styles.syncStateMessage}>Error: {loopError}</div>}
    </div>
  );
}
